import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Card,
	Collapse,
	Empty,
	Popover,
	Segmented,
	Space,
	Tooltip,
	Typography,
} from 'antd';
import {
	InfoCircleOutlined,
	PrinterOutlined,
	ReloadOutlined,
	RightOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import Header from '../../../../components/Header';
import {
	BottleneckSection,
	useGetBottleneckBoardQuery,
} from '../../../../redux/api/bottleneck-report';
import JobRow from './JobRow';
import PrintableBoard from './PrintableBoard';
import {
	BUCKET_COLORS,
	SEGMENT_LABELS,
	barScale,
	formatCount,
	formatDuration,
	formatMoney,
} from './buckets';
import {
	BAR_EXPLAINER,
	BUCKET_EXPLAINERS,
	PRECEDENCE_NOTE,
	QUARTILE_EXPLAINERS,
	TICKETED_EXPLAINER,
} from './explainers';
import './print.css';

const { Text, Title } = Typography;

/** Every print rule hangs off this, so no other page's printing changes. */
const PRINT_MODE_CLASS = 'bottleneck-print-mode';

/**
 * Portrait with a real margin, which the board needs and the rest of the app
 * must not inherit.
 *
 * This cannot live in `print.css` with everything else: `@page` takes no
 * selector, so it cannot be scoped to `PRINT_MODE_CLASS`, and `print.css` is
 * statically imported into the main bundle. A copy there would set the margin
 * for every print in the app, including label sheets that have to print edge
 * to edge. Injected on mount and removed on unmount instead.
 */
const PRINT_PAGE_RULE = '@page { size: portrait; margin: 0.5in; }';

const JOB_TYPES = ['Fastlane', 'Swiss', 'All'] as const;
type JobType = (typeof JOB_TYPES)[number];

/**
 * Fixed widths shared by the section headers and the caption strip above
 * them, so the twelve stages read as one table rather than twelve sentences.
 */
const CELL = {
	chevron: 'w-5 shrink-0',
	stage: 'w-52 shrink-0',
	lineItems: 'w-20 shrink-0 text-right tabular-nums',
	parts: 'w-24 shrink-0 text-right tabular-nums',
	revenue: 'w-28 shrink-0 text-right tabular-nums',
	tickets: 'w-20 shrink-0 text-right tabular-nums',
	quartile: 'w-16 shrink-0 text-right tabular-nums',
	samples: 'w-16 shrink-0 text-right tabular-nums',
};

/** `px-4` matches antd's collapse header padding, lining the strip up. */
const SectionCaptions = ({ showRevenue }: { showRevenue: boolean }) => (
	<div className="flex items-center gap-x-4 px-4 pb-1 text-[11px] uppercase tracking-wide text-neutral-400">
		<span className={CELL.chevron} />
		<span className={CELL.stage}>Stage</span>
		<span className={CELL.lineItems}>Line items</span>
		<span className={CELL.tickets}>Ticketed</span>
		<span className={CELL.parts}>Parts</span>
		{showRevenue && <span className={CELL.revenue}>Revenue</span>}
		<span className={CELL.quartile}>25%</span>
		<span className={CELL.quartile}>50%</span>
		<span className={CELL.quartile}>75%</span>
		<span className={CELL.quartile}>Max</span>
		<span className={CELL.samples}>Samples</span>
	</div>
);

/**
 * What the stage is for, what puts a job in it, and how to read its numbers.
 *
 * A Popover rather than a Tooltip because this is four distinct things and a
 * tooltip renders them as one wall of text.
 */
const SectionExplainer = ({ section }: { section: BottleneckSection }) => {
	// A bucket the server knows and this bundle does not -- a new stage, or a
	// stale cached bundle -- would otherwise throw inside render and take the
	// board down with it.
	const explainer = BUCKET_EXPLAINERS[section.key];
	if (!explainer) return null;

	return (
		<div className="max-w-sm text-xs">
			<p className="mb-2">{explainer.intent}</p>

			<p className="mb-1 font-medium">A job is here when</p>
			<ul className="mb-1 list-disc pl-4">
				{explainer.rules.map((rule) => (
					<li key={rule}>{rule}</li>
				))}
			</ul>
			<p className="mb-2 text-neutral-500">{PRECEDENCE_NOTE}</p>

			<p className="mb-1 font-medium">Ticketed</p>
			<p className="mb-2">{TICKETED_EXPLAINER}</p>

			<p className="mb-1 font-medium">The percentages</p>
			<p className="mb-2">{QUARTILE_EXPLAINERS[section.quartiles_basis]}</p>

			<p className="mb-1 font-medium">The bars</p>
			<p className="mb-0">{BAR_EXPLAINER}</p>
		</div>
	);
};

const SectionSummary = ({
	section,
	isOpen,
	showRevenue,
}: {
	section: BottleneckSection;
	isOpen: boolean;
	showRevenue: boolean;
}) => {
	const { quartiles } = section;
	const measured = quartiles.sample_count > 0;
	// An em-dash rather than a sentence: a section with nothing measured still
	// has to keep its cells, or the column below it bends around it.
	const duration = (seconds: number | null) =>
		measured ? formatDuration(seconds) : '—';

	return (
		<div className="flex items-center gap-x-4">
			<span className={CELL.chevron}>
				<RightOutlined
					className="text-[10px] text-neutral-400 transition-transform"
					rotate={isOpen ? 90 : 0}
				/>
			</span>

			<span className={`${CELL.stage} flex items-center gap-2 truncate`}>
				<span
					style={{
						width: 10,
						height: 10,
						borderRadius: 2,
						flex: 'none',
						background: BUCKET_COLORS[section.key],
					}}
				/>
				<span className="truncate font-medium">{section.label}</span>
				{section.quartiles_basis === 'lead_time' && (
					<Text type="secondary" className="text-xs">
						· lead time
					</Text>
				)}
			</span>

			<span className={CELL.lineItems}>
				{formatCount(section.line_item_count)}
			</span>
			{/*
			 * A dash rather than a zero. Eleven of the twelve stages are
			 * normally clean, and a column of noughts would bury the one that
			 * is not under numbers that all mean "nothing to see".
			 */}
			<span
				className={CELL.tickets}
				data-testid={`section-ticketed-${section.key}`}
			>
				{section.ticketed_line_item_count ? (
					<Text type="warning" className="font-medium">
						{formatCount(section.ticketed_line_item_count)}
					</Text>
				) : (
					<span className="text-neutral-300">—</span>
				)}
			</span>
			<span className={CELL.parts}>{formatCount(section.quantity)}</span>
			{showRevenue && (
				<span className={CELL.revenue}>{formatMoney(section.revenue)}</span>
			)}

			<span className={`${CELL.quartile} text-neutral-500`}>
				{duration(quartiles.p25_seconds)}
			</span>
			<span className={`${CELL.quartile} text-neutral-500`}>
				{duration(quartiles.p50_seconds)}
			</span>
			<span className={`${CELL.quartile} text-neutral-500`}>
				{duration(quartiles.p75_seconds)}
			</span>
			<span className={`${CELL.quartile} text-neutral-500`}>
				{duration(quartiles.p100_seconds)}
			</span>
			<span className={`${CELL.samples} text-neutral-400`}>
				{measured ? formatCount(quartiles.sample_count) : '—'}
			</span>
		</div>
	);
};

/**
 * Where undelivered work is piling up.
 *
 * Read-only by design: it exists to point at the stage that is holding the
 * shop back, and every number on it is produced server-side so the board and
 * the ERP cannot disagree.
 */
const JobBottleneck = () => {
	// Deliberately not persisted: the board opens on Fastlane every visit so
	// nobody inherits yesterday's filter and reads the wrong shop.
	const [jobType, setJobType] = useState<JobType>('Fastlane');
	const { data, isFetching, isError, refetch } = useGetBottleneckBoardQuery(
		jobType === 'All' ? {} : { productionMethods: [jobType] }
	);

	// The board opens closed: twelve one-line stages are the summary, and
	// opening one is the deliberate act of asking which jobs are behind it.
	const [openKeys, setOpenKeys] = useState<string[]>([]);

	// Print mode hides the live app, so it may only be on once there is a
	// report to put in its place. Switched on by `data` rather than by mount:
	// otherwise Ctrl+P while the board is still loading -- or after it failed,
	// when the error alert is the only thing worth reading -- prints a blank
	// sheet. The Print button is disabled without `data`, but the browser's own
	// print command is not.
	useEffect(() => {
		if (!data) return;
		const style = document.createElement('style');
		style.media = 'print';
		style.textContent = PRINT_PAGE_RULE;
		document.head.appendChild(style);
		document.body.classList.add(PRINT_MODE_CLASS);
		return () => {
			style.remove();
			document.body.classList.remove(PRINT_MODE_CLASS);
		};
	}, [data]);

	const sections = data?.sections ?? [];

	// One scale for the whole board, so a long bar means a long job rather
	// than a busy section.
	const { elapsedScale, remainingScale } = useMemo(() => {
		const elapsed: number[] = [];
		const remaining: number[] = [];
		sections.forEach((section) =>
			section.rows.forEach((row) => {
				let age = 0;
				row.segments.forEach((segment) => {
					if (segment.bucket === 'remaining') remaining.push(segment.seconds);
					else age += segment.seconds;
				});
				elapsed.push(age);
			})
		);
		return {
			elapsedScale: barScale(elapsed),
			remainingScale: barScale(remaining),
		};
	}, [sections]);

	// Delivered is excluded throughout: it is a fixed-size lookback at shipped
	// work, so folding it in would inflate "still in the shop".
	const totals = useMemo(
		() =>
			sections
				.filter((section) => section.key !== 'delivered')
				.reduce(
					(carry, section) => ({
						lineItems: carry.lineItems + section.line_item_count,
						parts: carry.parts + section.quantity,
						revenue: carry.revenue + Number(section.revenue ?? 0),
					}),
					{ lineItems: 0, parts: 0, revenue: 0 }
				),
		[sections]
	);

	// Empty sections cannot be opened, so they neither count towards "all
	// open" nor get pulled open by the toggle.
	const expandable = sections
		.filter((section) => section.rows.length > 0)
		.map((section) => section.key);
	const allOpen =
		expandable.length > 0 && expandable.every((key) => openKeys.includes(key));

	return (
		<>
			<Header
				title="Job Bottleneck"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: 'Reports' },
					{ title: 'Job Bottleneck' },
				]}
			/>

			{isError && (
				<Alert
					type="error"
					showIcon
					className="mb-4"
					message="The board could not be loaded."
					action={<Button onClick={() => refetch()}>Retry</Button>}
				/>
			)}

			{data?.live_truncated && (
				<Alert
					type="warning"
					showIcon
					className="mb-4"
					message={`Showing the first ${formatCount(
						data.live_limit
					)} live jobs — every count and quartile below is a floor, not a total.`}
					description="Live work is not expected to reach this ceiling. Hitting it usually means jobs are stuck open in the ERP rather than that the shop is that busy."
				/>
			)}

			<Space direction="vertical" size="middle" style={{ width: '100%' }}>
				<Card className="shadow-sm" size="small" loading={isFetching && !data}>
					<div className="flex flex-wrap items-center justify-between gap-4">
						<div>
							<Title level={5} style={{ margin: 0 }}>
								{data?.live_truncated ? 'At least ' : ''}
								{formatCount(totals.lineItems)} line items ·{' '}
								{formatCount(totals.parts)} parts
								{data?.include_revenue && (
									<> · {formatMoney(totals.revenue)}</>
								)}{' '}
								still in the shop
							</Title>
							<Text type="secondary">
								Delivered shows the last {data?.delivered_limit ?? 50} shipped.
								Cancelled, on-hold, completed and rejected work is left out.
							</Text>
						</div>
						<Space wrap>
							<Segmented
								value={jobType}
								options={[...JOB_TYPES]}
								onChange={(value) => {
									setJobType(value as JobType);
									// The new result set arrives closed rather than
									// inheriting whatever was open for the old one.
									setOpenKeys([]);
								}}
							/>
							{data && (
								<Text type="secondary">
									Updated {dayjs(data.generated_at).format('MMM D, HH:mm')}
								</Text>
							)}
							<Button
								disabled={!expandable.length}
								onClick={() => setOpenKeys(allOpen ? [] : expandable)}
							>
								{allOpen ? 'Collapse all' : 'Expand all'}
							</Button>
							<Button
								icon={<PrinterOutlined />}
								disabled={!data}
								onClick={() => window.print()}
							>
								Print
							</Button>
							<Button
								icon={<ReloadOutlined />}
								loading={isFetching}
								onClick={() => refetch()}
							>
								Refresh
							</Button>
						</Space>
					</div>

					<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
						{sections.map((section) => (
							<span key={section.key} className="flex items-center gap-1.5">
								<span
									style={{
										width: 10,
										height: 10,
										borderRadius: 2,
										background: BUCKET_COLORS[section.key],
									}}
								/>
								{section.label}
							</span>
						))}
						<Tooltip title={BAR_EXPLAINER}>
							<span className="flex items-center gap-1.5 text-neutral-500">
								<InfoCircleOutlined /> How to read the bars
							</span>
						</Tooltip>
					</div>
				</Card>

				{!isFetching && data && !totals.lineItems && (
					<Card className="shadow-sm">
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description="Nothing is in progress right now."
						/>
					</Card>
				)}

				{data && (
					<div>
						<SectionCaptions showRevenue={data.include_revenue} />
						<Collapse
							activeKey={openKeys}
							onChange={(keys) =>
								setOpenKeys(Array.isArray(keys) ? keys : [keys])
							}
							className="bg-white shadow-sm"
							items={sections.map((section) => ({
								key: section.key,
								label: (
									<SectionSummary
										section={section}
										isOpen={openKeys.includes(section.key)}
										showRevenue={data.include_revenue}
									/>
								),
								// Always present: the intent and the rules are worth
								// reading even for a stage with nothing measured.
								extra: (
									<Popover
										title={section.label}
										placement="leftTop"
										content={<SectionExplainer section={section} />}
									>
										<InfoCircleOutlined className="text-neutral-400" />
									</Popover>
								),
								// antd's own arrow sits in a wrapper the caption strip
								// cannot be lined up against, so the header renders one.
								showArrow: false,
								collapsible: section.rows.length ? undefined : 'disabled',
								children: section.rows.length ? (
									<div>
										<div className="flex items-center gap-2 border-b px-2 pb-1 text-[11px] uppercase tracking-wide text-neutral-400">
											<span className="w-44 shrink-0">Order</span>
											<span className="w-40 shrink-0">Part</span>
											<span className="w-14 shrink-0 text-right">Qty</span>
											{data.include_revenue && (
												<span className="w-20 shrink-0 text-right">
													Revenue
												</span>
											)}
											<span className="w-20 shrink-0 text-right">Due</span>
											<span className="flex-1 text-center">
												History · now · remaining
											</span>
											<span className="w-8 shrink-0 text-center">Tkt</span>
											<span className="w-16 shrink-0 text-right">In stage</span>
										</div>
										{section.rows.map((row) => (
											<JobRow
												key={row.id}
												row={row}
												elapsedScale={elapsedScale}
												remainingScale={remainingScale}
												showRevenue={data.include_revenue}
											/>
										))}
									</div>
								) : null,
							}))}
						/>
					</div>
				)}

				<Text type="secondary" className="text-xs">
					{SEGMENT_LABELS.uncategorized} collects jobs whose statuses do not
					describe any stage — most often programming finished with no material
					reserved and no purchase order open. Treat it as a punch list.
				</Text>
			</Space>

			{data && <PrintableBoard data={data} jobType={jobType} totals={totals} />}
		</>
	);
};

export default JobBottleneck;
